import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatListOption } from '@angular/material/list';
import { ApplicationPermissions, Group, Permission } from '../../../../models/application-permissions.model';
import { AppConfigService, ApplicationId, Constants, MarketDataPermissions, ProviderType, SecurityService } from 'common';
import { ContactService } from '../../../../services/contact.service';

@Component({
  selector: 'acc-contact-permissions',
  templateUrl: './contact-permissions.component.html',
  styleUrl: './contact-permissions.component.scss',
})
export class ContactPermissionsComponent implements OnInit {
  @Input() contactEmail: string;
  @Input() permissionSet: number;
  @Input() formDisabled: boolean = true;
  @Input() applicationPermissions: ApplicationPermissions;

  private allPermissions: Permission[] = [];
  private originalPermissions: number;
  isMarketData: boolean = false;
  marketPermissionsForAccount: MarketDataPermissions[] = [];

  Permissions = Constants.permissionGroups;

  constructor(
    private contactService: ContactService,
    private changeDetector: ChangeDetectorRef,
    private env: AppConfigService,
    private security: SecurityService
  ) { }

  ngOnInit() {
    this.isMarketData =
      this.env.config.applicationId === ApplicationId.DataPortal && this.security.profile.selectedAccount.providerType === ProviderType.MARKETDATA;

    if (this.isMarketData) {
      this.contactService.getMarketsAvailableByAccountCode(this.security.isSuperUser).subscribe((markets) => {
        markets.forEach((market) => {
          this.marketPermissionsForAccount.push(this.contactService.mapMarketIdToPermission(market.marketId));
        });
      });
    }

    if (this.hasOnlineAccess) {
      this.originalPermissions = this.permissionSet;
      this.allPermissions = this.applicationPermissions.groups.reduce((acc: Permission[], val) => acc.concat(val.permissions), []);
      this.initialisePermissions();
      this.contactService.validationTriggered.subscribe((result: number[]) => {
        this.updateValidationResults(result);
      });
    }
  }

  toggleGroups = (allSelected: boolean): void => {
    if (!this.isMarketData) {
      //Can be removed when code reverted
      if (allSelected) {
        this.permissionSet = 0;
        this.applicationPermissions.noOnlineUsers = false;
        for (let permission of this.allPermissions) {
          permission.selected =
            (this.permissionSet !== this.Permissions.hasNoOnlineAccess && this.permissionSet & permission.permissionValue) ===
            permission.permissionValue;
          if (permission.selected && permission.invalid) {
            permission.invalid = false;
          }
        }
        this.applicationPermissions.groups.forEach((group) => this.toggleGroup(group, allSelected));
      } else {
        this.permissionSet = this.Permissions.hasNoOnlineAccess;
        this.reset(this.Permissions.hasNoOnlineAccess);
      }
      this.contactService.changePermission(this.permissionSet);
      this.changeDetector.detectChanges();
    }
  };

  get hasOnlineAccess(): boolean {
    return this.applicationPermissions.groups?.length > 0;
  }

  get indeterminateGroups(): boolean {
    //Can be removed when code reverted
    return this.applicationPermissions.groups.every((group) => group.indeterminate);
  }

  get canEditOnlineAccess(): boolean {
    //A user should not be able to remove their online access from online as it will shut them out!
    var isCurrentUser: boolean = this.security.profile.user.email?.toLocaleLowerCase()?.trim() === this.contactEmail?.toLocaleLowerCase()?.trim();
    return !(this.formDisabled || isCurrentUser) || this.security.profile.user.isSuperUser;
  }

  private calculatIndeterminateGroups() {
    for (let group of this.applicationPermissions.groups) {
      let allSelected = true;
      let noneSelected = true;
      const existingPermissions = this.permissionSet === this.Permissions.hasNoOnlineAccess ? this.Permissions.hasOnlineAccess : this.permissionSet;
      for (let permission of group.permissions) {
        if (this.permissionValidForCurrentAccount(group.groupId, permission.permissionValue)) {
          allSelected = allSelected && (existingPermissions & permission.permissionValue) === permission.permissionValue;
          noneSelected = noneSelected && (existingPermissions & permission.permissionValue) !== permission.permissionValue;
        }
      }
      group.allSelected = allSelected;
      group.indeterminate = !allSelected && !noneSelected;
    }
  }

  isPermissionGroupEnabled(permissionGroup: number): boolean {
    //This is hard coded for now, we just want to prevent primary user from being set/changed through the UI, but in the future we may want to back this up
    //with a DB table or similar
    return this.security.profile.user.isSuperUser || permissionGroup !== 1;
  }

  isPermissionEnabled(permission: number): boolean {
    //This is hard coded for now, we just want to prevent primary user from being set through the UI, but in the future we may want to back this up
    //with a DB table or similar
    return this.security.profile.user.isSuperUser || permission !== Constants.permissionGroups.administration.canModifyOnlineAccess;
  }

  permissionValidForCurrentAccount(groupId: number, permissionValue: number): boolean {
    //This is hard coded for now, we just want to hide the permissions shown for an account to ensure the user can only be granted permissions
    //they have access to. It only applies in Market Data too for now.
    if (this.isMarketData) {
      if (groupId === 2) {
        if (!this.marketPermissionsForAccount || this.marketPermissionsForAccount.length === 0) {
          return true;
        } else {
          return this.marketPermissionsForAccount.find((market) => market === permissionValue) != null;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  initialisePermissions(): void {
    const assignedPermissions: number = this.permissionSet;
    for (let permission of this.allPermissions) {
      permission.selected =
        (assignedPermissions !== this.Permissions.hasNoOnlineAccess && assignedPermissions & permission.permissionValue) ===
        permission.permissionValue;
    }
    this.calculatIndeterminateGroups();
  }

  emitPermissionsChanged(selectedOptions: MatListOption[]) {
    let permissionList = selectedOptions.map<number>((o) => +o.value);
    if (permissionList.includes(this.Permissions.hasOnlineAccess)) {
      this.permissionSet = permissionList.reduce((a, b) => a + b);
      this.applicationPermissions.noOnlineUsers = false;
      for (let permission of this.allPermissions) {
        permission.selected =
          (this.permissionSet !== this.Permissions.hasNoOnlineAccess && this.permissionSet & permission.permissionValue) ===
          permission.permissionValue;
        if (permission.selected && permission.invalid) {
          permission.invalid = false;
        }
      }
    } else {
      this.permissionSet = this.Permissions.hasNoOnlineAccess;
      this.reset(this.Permissions.hasNoOnlineAccess);
    }

    this.contactService.changePermission(this.permissionSet);
    this.changeDetector.detectChanges();
  }

  toggleGroup(group: Group, allSelected: boolean) {
    const assignedPermissions = this.permissionSet;
    let newPermissions = assignedPermissions;
    for (let permission of group.permissions) {
      if (this.permissionValidForCurrentAccount(group.groupId, permission.permissionValue)) {
        const alreadySelected = (this.permissionSet & permission.permissionValue) === permission.permissionValue;
        if (allSelected && !alreadySelected) {
          newPermissions += permission.permissionValue;
        }
        if (!allSelected && alreadySelected) {
          newPermissions -= permission.permissionValue;
        }
      }
    }
    this.permissionSet = newPermissions;
    this.initialisePermissions();
    this.contactService.changePermission(this.permissionSet);
  }

  updateValidationResults(validationResults: number[]) {
    if (validationResults.length != 0) {
      this.applicationPermissions.noOnlineUsers = validationResults.includes(this.Permissions.hasOnlineAccess);
      for (let permission of this.allPermissions) {
        permission.invalid = validationResults.includes(permission.permissionValue);
      }
    }
  }

  reset(toPermissionSet: number): void {
    this.permissionSet = toPermissionSet;
    if (this.hasOnlineAccess) this.initialisePermissions();
  }
}
